import type { MVPYearlyMileage_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { OpcCoverableAttribute } from '../../../types';
import { Grid, GridItem, NumberField, SelectField } from '@flos/react-ui';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useRef, useState } from 'react';
import { useHippoText } from '../../../../../hooks/useHippoText';
import { isOpcDataFilledAtom } from '../../../../../recoil/atoms/isOpcDataFilledAtom';
import { useYearlyMileagesAndOdometerReading } from '../../../../../hooks/useYearlyMileagesAndOdometerReading';
import { isOpcFetchingAtom } from '../../../../../recoil/atoms/isOpcFetchingAtom';

enum ODOMETER_READING_ERROR {
    EMPTY_ODOMETER = 'Empty Odometer reading',
    INT_PARSE = 'Cannot parse odometer reading to Integer',
    REQUIRE_NEW_ODOMETER = 'New yearly mileage require new odometer reading',
    NEW_ODOMETER_LOWER_THAN_PREVIOUSLY = 'New odometer reading must be higher than current odometer reading'
}

const CarYearlyMileageChange = ({
    updateCoverableAttribute
}: {
    updateCoverableAttribute: (attribute: OpcCoverableAttribute) => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);

    const [odometerError, setOdometerError] = useState<string>('');
    const { options, suggestedMileage, setSuggestedMileage, canSaveOdometerAndMileage } =
        useYearlyMileagesAndOdometerReading();
    const odometerReadingRef = useRef<HTMLInputElement>(null);
    const setIsOpcDataFilled = useSetRecoilState(isOpcDataFilledAtom);

    const errorMessage: Record<string, string> = {
        [ODOMETER_READING_ERROR.EMPTY_ODOMETER]: '',
        [ODOMETER_READING_ERROR.INT_PARSE]: '',
        [ODOMETER_READING_ERROR.REQUIRE_NEW_ODOMETER]:
            'Kilometerstand skal ændres når kilometerforbrug ændres.',
        [ODOMETER_READING_ERROR.NEW_ODOMETER_LOWER_THAN_PREVIOUSLY]: getHippoText(
            'DSS-ADJUST-YEARLY-MILEAGE-ODOMETER-READING-FIELD-VALIDATION-TEXT'
        )
    };

    const handleValidateOdometerReading = async (
        odometerValue: string,
        yearlyMileage: MVPYearlyMileage_Top
    ): Promise<void> => {
        await canSaveOdometerAndMileage(odometerValue, yearlyMileage).then((invalidReason) => {
            setOdometerError(invalidReason);
            if (invalidReason) {
                setIsOpcDataFilled(false);
                return;
            }
            updateCoverableAttribute({
                odometerReading: odometerValue,
                yearlyMileage
            });
            setIsOpcDataFilled(true);
        });
    };

    const handleYearlyMileageChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedIndex = e.target.selectedIndex;
        const { value } = e.target.options[selectedIndex];
        setSuggestedMileage(value as MVPYearlyMileage_Top);
        const odometerValue = odometerReadingRef.current?.value.replaceAll('.', '') ?? '';
        void handleValidateOdometerReading(odometerValue, value as MVPYearlyMileage_Top);
    };

    const handleOdometerFieldBlur = (e: React.FocusEvent<HTMLInputElement, Element>): void => {
        setOdometerError('');
        const updatedOdometerValue = e.target.value.replaceAll('.', '');

        if (updatedOdometerValue.length <= 0) {
            setOdometerError(ODOMETER_READING_ERROR.EMPTY_ODOMETER);
            return;
        }
        void handleValidateOdometerReading(updatedOdometerValue, suggestedMileage);
    };

    const isEmptyOdometer = [
        ODOMETER_READING_ERROR.EMPTY_ODOMETER,
        ODOMETER_READING_ERROR.INT_PARSE
    ].includes(odometerError as ODOMETER_READING_ERROR);

    return (
        <>
            <Grid gap={6}>
                <GridItem span={12}>
                    <h6>{getHippoText('DSS-ADJUST-YEARLY-MILEAGE-TEXT')}</h6>
                    <SelectField
                        label={getHippoText('DSS-ADJUST-YEARLY-MILEAGE-FIELD')}
                        options={options}
                        id="carYearlyMileage"
                        name="carYearlyMileage"
                        onChange={handleYearlyMileageChange}
                        value={suggestedMileage}
                        disabled={isOpcFetching}
                    />
                </GridItem>

                <GridItem span={12}>
                    <h6>{getHippoText('DSS-ADJUST-YEARLY-MILEAGE-ODOMETER-READING-TEXT')}</h6>
                    <NumberField
                        ref={odometerReadingRef}
                        label={getHippoText('DSS-ADJUST-YEARLY-MILEAGE-ODOMETER-READING-FIELD')}
                        id="carOdometerReading"
                        name="carOdometerReading"
                        withFormatting={true}
                        isValid={!odometerError}
                        onFocus={() => {
                            setIsOpcDataFilled(false);
                        }}
                        {...(isEmptyOdometer ? {} : { errorText: '' })}
                        onBlur={handleOdometerFieldBlur}
                        disabled={isOpcFetching}
                    />
                    {!!odometerError && !isEmptyOdometer && (
                        <p className="paragraph--small u-color-supplement-error-base">
                            {errorMessage[odometerError]}
                        </p>
                    )}
                </GridItem>
            </Grid>
        </>
    );
};

export default CarYearlyMileageChange;
